import * as signalR from '@microsoft/signalr';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getSignalRConnectionString, isInvocationGroupTypeValidForNotificationType, SocketNotificationTypes } from './index';

const MAX_RETRIES = 5;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;

export const useSocketNotifications = (params) => {
  const {
    type,
    invocationGroup = null,
    autoDisconnectOnUnmount = true,
    onNotificationReceived,
  } = params;

  if (!Object.values(SocketNotificationTypes).includes(type)) {
    throw new Error(`Notification type ${type} is not allowed.`);
  }

  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const [connectionFailed, setConnectionFailed] = useState(false);

  const signalRConnection = useMemo(() => {
    const connectionString = getSignalRConnectionString(type);

    if (!connectionString) {
      throw new Error(`No connection string found for notification type ${type}.`);
    }

    return new signalR.HubConnectionBuilder()
      .withUrl(connectionString, {
        withCredentials: false,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .build();
  }, [type]);

  const checkAndJoinGroupIfRequired = useCallback(() => {
    if (signalRConnection.state !== signalR.HubConnectionState.Connected) {
      console.error('Socket connection is not initialized yet to join group');
      return;
    }

    if (invocationGroup?.type && invocationGroup?.id && isInvocationGroupTypeValidForNotificationType(type, invocationGroup.type)) {
      signalRConnection.invoke(invocationGroup.type, invocationGroup.id);
    }
  }, [signalRConnection, type, invocationGroup]);

  const onConnectionReconnected = useCallback(() => {
    checkAndJoinGroupIfRequired();
  }, [checkAndJoinGroupIfRequired]);

  const onConnectionClosed = useCallback((error) => {
    // Should test if this happens and decide on what needs to done
    console.error('SignalR connection closed:', error);
  }, []);

  const subscribeToNotification = useCallback(() => {
    if (signalRConnection.state !== signalR.HubConnectionState.Connected) {
      console.error('Socket connection is not initialized yet to subscribe');
      return;
    }

    signalRConnection.on(type, onNotificationReceived);
  }, [signalRConnection, type, onNotificationReceived]);

  const unSubscribeToNotification = useCallback(() => {
    if (signalRConnection.state !== signalR.HubConnectionState.Connected) {
      console.error('Socket connection is not initialized yet to unsubscribe');
      return;
    }

    signalRConnection.off(type);
  }, [signalRConnection, type]);

  const initializeSocketConnection = useCallback(async () => {
    if (signalRConnection.state === signalR.HubConnectionState.Connected) {
      console.error('There is an active socket connection. Can not initialize again.');
      return;
    }
    if (retryTimerRef.current) {
      return;
    }
    if (retryCountRef.current >= MAX_RETRIES) {
      console.error(`SignalR: max retries (${MAX_RETRIES}) reached. Giving up.`);
      setConnectionFailed(true);
      return;
    }

    try {
      await signalRConnection.start();
      retryCountRef.current = 0;
      setConnectionFailed(false);
      subscribeToNotification();
      checkAndJoinGroupIfRequired();
      signalRConnection.onreconnected(onConnectionReconnected);
      signalRConnection.onclose(onConnectionClosed);
    } catch (error) {
      retryCountRef.current += 1;
      const delay = Math.min(
        BASE_DELAY_MS * (2 ** (retryCountRef.current - 1)),
        MAX_DELAY_MS,
      );
      console.error(
        `SignalR connection failed (attempt ${retryCountRef.current}/${MAX_RETRIES}). Retrying in ${delay}ms.`,
        error,
      );
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        initializeSocketConnection();
      }, delay);
    }
  }, [signalRConnection, subscribeToNotification, checkAndJoinGroupIfRequired, onConnectionClosed, onConnectionReconnected]);

  const terminateSocketConnection = useCallback(async () => {
    if (signalRConnection.state !== signalR.HubConnectionState.Connected) {
      console.error('There is no active socket connection to terminate');
      return;
    }

    try {
      await signalRConnection.stop();
    } catch (error) {
      console.error('Error stopping SignalR connection:', error);
    }
  }, [signalRConnection]);

  const canInitiateConnection = useCallback(() => {
    return signalRConnection.state === signalR.HubConnectionState.Disconnected && !connectionFailed;
  }, [signalRConnection, connectionFailed]);

  const canTerminateConnection = useCallback(() => {
    return signalRConnection.state === signalR.HubConnectionState.Connected;
  }, [signalRConnection]);

  useEffect(() => {
      return () => {
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
        }
        if (signalRConnection.state === signalR.HubConnectionState.Connected) {
          unSubscribeToNotification();
          if (autoDisconnectOnUnmount) {
            terminateSocketConnection();
          }
        }
      };
  }, [signalRConnection, terminateSocketConnection, unSubscribeToNotification, autoDisconnectOnUnmount]);

  return {
    initializeSocketConnection,
    terminateSocketConnection,
    subscribeToNotification,
    unSubscribeToNotification,
    canInitiateConnection,
    canTerminateConnection,
    connectionFailed,
  };
};
